import React, { useState, useEffect } from 'react';
import { Menu } from 'lucide-react';
import { ChatInterfaceV2 } from './components/ChatInterfaceV2';
import { Sidebar } from './components/Sidebar';
import { WebSocketService } from './services/websocketService';
import { Family } from './types';
import { appConfig } from './config';
import './App.css';

const API_BASE = appConfig.API_BASE;
const WS_BASE = appConfig.WS_BASE;

const wsClient = new WebSocketService(API_BASE, WS_BASE);

function App() {
  const [families, setFamilies] = useState<Family[]>([]);
  const [selectedFamilyId, setSelectedFamilyId] = useState<string | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    wsClient.connect();
    loadFamilies();

    wsClient.on('child_created', () => loadFamilies());
    wsClient.on('child_deleted', () => loadFamilies());
    wsClient.on('family_created', () => loadFamilies());

    return () => {
      wsClient.disconnect();
    };
  }, []);

  useEffect(() => {
    if (families.length > 0 && !selectedFamilyId) {
      handleFamilySelect(families[0].id);
    }
  }, [families, selectedFamilyId]);

  const loadFamilies = async () => {
    try {
      const baseUrl = API_BASE.replace(/\/$/, '');
      const response = await fetch(`${baseUrl}/export/families`);
      if (response.ok) {
        const data = await response.json();
        setFamilies(data.families || []);
      }
    } catch (error) {
      console.error('Failed to load families:', error);
    }
  };

  const handleFamilySelect = (familyId: string) => {
    setSelectedFamilyId(familyId);
    wsClient.switchFamily(familyId);
  };

  const currentFamily = families.find(f => f.id === selectedFamilyId);
  const headerTitle = currentFamily?.parent_name || 'HappyFamilyAI';

  return (
    <div className="app-container">
      {/* Header */}
      <header className="app-header">
        <button 
          className="menu-button"
          onClick={() => setSidebarOpen(true)}
          aria-label="Open menu"
        >
          <Menu size={20} />
        </button>
        
        <div className="header-title">
          {headerTitle}
        </div>
        
        <div className="header-spacer" />
      </header>

      {/* Main Chat Area */}
      <main className="main-content">
        <ChatInterfaceV2
          familyId={selectedFamilyId}
          apiBase={API_BASE}
        />
      </main>

      {/* Sidebar */}
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        families={families}
        currentFamilyId={selectedFamilyId}
        onSelectFamily={handleFamilySelect}
      />
    </div>
  );
}

export default App;
